import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { KpiCard } from "./KpiCard";
import { tokens } from "../theme";
import { platformLabel } from "../utils/devicePresentation";
import type { DevicePlatform, EnergyReport } from "@shared/types/foyer";

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                          */
/* -------------------------------------------------------------------------- */

/** kWh with sensible precision: 1 dp under 100, whole numbers above. */
export function formatKWh(fmt: Formatters, n: number): string {
  return n < 100
    ? fmt.number(n, { maximumFractionDigits: 1 })
    : fmt.number(Math.round(n));
}

/** CO₂ in kg, or tonnes once it's large enough to read better that way. */
export function formatCo2(t: Translate, fmt: Formatters, kg: number): string {
  if (kg >= 1000) {
    return t("analytics:energyReportPanel.co2Tonnes", {
      value: fmt.number(kg / 1000, { maximumFractionDigits: 1 }),
    });
  }
  return t("analytics:energyReportPanel.co2Kg", {
    value: fmt.number(kg, { maximumFractionDigits: 1 }),
  });
}

/* -------------------------------------------------------------------------- */
/* Building blocks                                                             */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/* ---- Single-series area chart (kWh over time) ---------------------------- */
function AreaChart({
  labels,
  data,
  color,
  unit,
}: {
  labels: string[];
  data: number[];
  color: string;
  unit: string;
}) {
  const { t } = useTranslation("analytics");
  const W = 640;
  const H = 240;
  const padX = 8;
  const padTop = 12;
  const padBottom = 28;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const n = labels.length;
  if (n === 0) return <Box sx={{ height: H }} />;
  const max = Math.max(...data, 0.001) * 1.15;
  const x = (i: number) => padX + (innerW * i) / Math.max(1, n - 1);
  const y = (v: number) => padTop + innerH - (v / max) * innerH;
  const line = data
    .map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`)
    .join(" ");
  const area =
    `M ${x(0)} ${y(0)} ` +
    data.map((v, i) => `L ${x(i)} ${y(v)}`).join(" ") +
    ` L ${x(n - 1)} ${y(0)} Z`;
  // Keep the x-axis from overflowing on the 30-day range.
  const tickEvery = Math.ceil(n / 12);

  return (
    <Box>
      <Box sx={{ width: "100%" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          role="img"
          aria-label={t("energyReportPanel.chartAriaLabel", { unit })}
        >
          {[0, 0.5, 1].map((g) => (
            <line
              key={g}
              x1={padX}
              x2={W - padX}
              y1={padTop + innerH * g}
              y2={padTop + innerH * g}
              style={{ stroke: tokens.divider }}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <path d={area} style={{ fill: color, fillOpacity: 0.18 }} />
          <path
            d={line}
            fill="none"
            style={{ stroke: color }}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0.5, mt: 0.5 }}
      >
        {labels.map((l, i) => (
          <Typography
            key={`${l}-${i}`}
            variant="caption"
            sx={{
              typography: "micro",
              visibility: i % tickEvery === 0 ? "visible" : "hidden",
            }}
          >
            {l}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/* ---- Horizontal bar list (kWh) ------------------------------------------- */
function BarList({
  items,
  emptyLabel,
}: {
  items: { name: string; kWh: number; co2Kg: number }[];
  emptyLabel: string;
}) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const max = Math.max(...items.map((i) => i.kWh), 0.001);
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {items.slice(0, 8).map((it) => (
        <Box key={it.name}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: "fontWeightMedium", mr: 1 }}
            >
              {it.name}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
              {t("energyReportPanel.barValue", {
                kwh: formatKWh(fmt, it.kWh),
                co2: formatCo2(t, fmt, it.co2Kg),
              })}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: tokens.rowAlt,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(it.kWh / max) * 100}%`,
                height: "100%",
                borderRadius: 99,
                background: `linear-gradient(90deg, ${tokens.success}, ${tokens.brand})`,
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/* Panel                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Presentational energy report - KPIs, consumption chart, breakdowns and the
 * per-device table. Shared by the premise-scoped {@link EnergyView} and the
 * company chain dialog. `scope` flips the premise-aware bits: a chain shows the
 * per-premise breakdown + a Premises KPI + a Premise column; a single property
 * shows a Devices KPI + a Room-led table.
 */
export default function EnergyReportPanel({
  report,
  scope,
}: {
  report: EnergyReport;
  scope: "premise" | "company";
}) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const isChain = scope === "company";
  const kwh = (n: number) =>
    t("energyReportPanel.kwh", { value: formatKWh(fmt, n) });
  const co2 = (kg: number) => formatCo2(t, fmt, kg);

  const noneControlled =
    report.totals.deviceCount > 0 &&
    report.totals.controllableDeviceCount === 0;

  const platformItems = useMemo(
    () =>
      report.byPlatform.map((p) => ({
        name:
          p.name === "unknown"
            ? t("energyReportPanel.unknownPlatform")
            : platformLabel(p.name as DevicePlatform),
        kWh: p.kWh,
        co2Kg: p.co2Kg,
      })),
    [report, t]
  );

  return (
    <Stack spacing={2.5}>
      {/* KPI row */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("energyReportPanel.kpi.energyUsed")}
            value={kwh(report.totals.kWh)}
            sub={t(`energyReportPanel.rangeNoun.${report.range}`)}
            icon={<BoltOutlinedIcon />}
            tint={tokens.warningTint}
            accent={tokens.warning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("energyReportPanel.kpi.co2")}
            value={co2(report.totals.co2Kg)}
            sub={t("energyReportPanel.kpi.co2Sub", {
              intensity: fmt.number(report.gridIntensity),
            })}
            icon={<Co2OutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("energyReportPanel.kpi.saved")}
            value={kwh(report.totals.savedKwh)}
            sub={
              noneControlled
                ? t("energyReportPanel.kpi.noneControllable")
                : t("energyReportPanel.kpi.savedSub", {
                    pct: fmt.number(report.totals.savedPct / 100, {
                      style: "percent",
                      maximumFractionDigits: 0,
                    }),
                    co2: co2(report.totals.savedCo2Kg),
                  })
            }
            icon={<SavingsOutlinedIcon />}
            tint={noneControlled ? undefined : tokens.successTint}
            accent={noneControlled ? undefined : tokens.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isChain ? (
            <KpiCard
              label={t("energyReportPanel.kpi.premises")}
              value={fmt.number(report.totals.premiseCount)}
              sub={t("energyReportPanel.kpi.premisesSub", {
                count: report.totals.deviceCount,
              })}
              icon={<ApartmentOutlinedIcon />}
            />
          ) : (
            <KpiCard
              label={t("energyReportPanel.kpi.devices")}
              value={fmt.number(report.totals.deviceCount)}
              sub={t("energyReportPanel.kpi.devicesSub")}
              icon={<DevicesOtherOutlinedIcon />}
            />
          )}
        </Grid>
      </Grid>

      {/* Consumption over time + platform split */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard
            title={t("energyReportPanel.consumption.title")}
            subtitle={
              report.range === "day"
                ? t("energyReportPanel.consumption.perHour")
                : t("energyReportPanel.consumption.perDay")
            }
          >
            <AreaChart
              labels={report.series.labels}
              data={report.series.kWh}
              color={tokens.success}
              unit="kWh"
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title={t("energyReportPanel.byPlatform")}>
            <BarList
              items={platformItems}
              emptyLabel={t("energyReportPanel.noDevices")}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Per-premise breakdown - chain only */}
      {isChain && (
        <SectionCard title={t("energyReportPanel.byPremise")}>
          <BarList
            items={report.byPremise}
            emptyLabel={t("energyReportPanel.noPremises")}
          />
        </SectionCard>
      )}

      {/* Device table */}
      <SectionCard
        title={t("energyReportPanel.devices.title")}
        subtitle={t("energyReportPanel.devices.subtitle")}
      >
        {report.devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("energyReportPanel.devices.empty")}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader data-testid="energy-table">
              <TableHead>
                <TableRow>
                  {isChain && (
                    <TableCell>
                      {t("energyReportPanel.devices.columns.premise")}
                    </TableCell>
                  )}
                  <TableCell>
                    {t("energyReportPanel.devices.columns.unit")}
                  </TableCell>
                  <TableCell>
                    {t("energyReportPanel.devices.columns.model")}
                  </TableCell>
                  <TableCell align="right">
                    {t("energyReportPanel.devices.columns.draw")}
                  </TableCell>
                  <TableCell align="right">
                    {t("energyReportPanel.devices.columns.kwh")}
                  </TableCell>
                  <TableCell align="right">
                    {t("energyReportPanel.devices.columns.co2")}
                  </TableCell>
                  <TableCell>
                    {t("energyReportPanel.devices.columns.state")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.devices.map((d) => (
                  <TableRow key={d.screenId} hover>
                    {isChain && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="fontWeightSemibold"
                        >
                          {d.premiseName}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>{d.roomId ?? "-"}</TableCell>
                    <TableCell>{d.model ?? "-"}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={t("energyReportPanel.devices.drawTooltip", {
                          on: fmt.number(d.profile.onWatts),
                          standby: fmt.number(d.profile.standbyWatts),
                          source: d.profile.source,
                        })}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t("energyReportPanel.devices.watts", {
                            value: fmt.number(d.profile.onWatts),
                          })}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{formatKWh(fmt, d.kWh)}</TableCell>
                    <TableCell align="right">{co2(d.co2Kg)}</TableCell>
                    <TableCell>
                      {!d.powerControlled ? (
                        // No power control on this device: a schedule can't
                        // park it, so it never contributes a saving. Say that
                        // outright rather than show a state we can't act on.
                        <Tooltip
                          title={t(
                            "energyReportPanel.devices.noPowerControlTooltip"
                          )}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(
                              "energyReportPanel.devices.noPowerControl"
                            )}
                          />
                        </Tooltip>
                      ) : d.powerState ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={d.powerState === "on" ? "warning" : "default"}
                          label={
                            d.powerState === "on"
                              ? t("energyReportPanel.devices.on")
                              : t("energyReportPanel.devices.standby")
                          }
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <Typography variant="caption" color="text.secondary">
        {t("energyReportPanel.footnote", {
          intensity: fmt.number(report.gridIntensity),
        })}
      </Typography>
    </Stack>
  );
}
